import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getOnboardingChecklist } from "../api/onboardingApi";
import { useEplexAccountInfoStore } from "./eplex/eplexAccountInfo";
import type { OnboardingQuestItem } from "../models/onboarding/OnboardingTypes";

type ChecklistStatus = "idle" | "loading" | "ready" | "error";

/**
 * Чек-лист квестов онбординга.
 *
 * Участие проверяется ЗДЕСЬ, у самих данных, а не только у разметки (CAB-734).
 * Проверка в шаблоне закрывает ровно одно место показа: второй точке вывода,
 * прямому вызову из чужого кода или старой сборке, живущей в давно открытой
 * вкладке, она не мешает — а без данных показывать нечего никому. По этой же
 * причине признак не спрашивается один раз на входе: `load()` сверяется с ним
 * на каждом вызове, поэтому наградный опрос сам гасит чек-лист, как только
 * участие снято.
 */
export const useOnboardingStore = defineStore("onboarding", () => {
  const items = ref<OnboardingQuestItem[]>([]);
  const status = ref<ChecklistStatus>("idle");

  let inflight: Promise<void> | null = null;

  const completedCount = computed(() => items.value.filter((item) => item.status === "completed").length);
  const totalCount = computed(() => items.value.length);
  const hasItems = computed(() => status.value === "ready" && items.value.length > 0);

  // Сигнал "открой панель и подсвети квест N" для клика по наградному тосту
  // (см. useOnboardingRewardWatcher). Инкремент токена — а не просто
  // highlightedQuestId — чтобы повторный клик по тосту на тот же квест тоже
  // срабатывал (watch на одно и то же значение second раз не сработает).
  const openRequestToken = ref(0);
  const highlightedQuestId = ref<string | null>(null);

  // Очередь наградных модалок (ТЕСТ ОНБОРДИНГ 2, «крупные» квесты) — не более
  // одной на экране одновременно, показ отдельно гейтится в RewardModal.vue
  // состоянием тура (см. useGuidanceStore().isActive).
  const rewardModalQueue = ref<OnboardingQuestItem[]>([]);

  async function load(force = false): Promise<void> {
    if (!useEplexAccountInfoStore().isOnboardingParticipant) {
      reset();
      return;
    }

    if (!force && (status.value === "ready" || status.value === "loading")) {
      await (inflight ?? Promise.resolve());
      return;
    }

    status.value = status.value === "ready" ? status.value : "loading";
    inflight = (async () => {
      try {
        const loaded = await getOnboardingChecklist();

        // Ответ, пришедший уже после снятия участия, нам не принадлежит:
        // принять его — значит вернуть чек-лист на экран сразу после того, как
        // его погасили. Запрос мог уйти последним участником этой вкладки
        if (!useEplexAccountInfoStore().isOnboardingParticipant) {
          reset();

          return;
        }

        items.value = loaded;
        status.value = "ready";
      } catch (error) {
        console.error("Onboarding checklist: failed to load", error);
        if (items.value.length === 0) {
          items.value = [];
        }
        status.value = "error";
      }
    })();

    await inflight;
  }

  /**
   * Возвращает стор к состоянию «квестов нет». Зовётся при снятии признака
   * участия и при смене личности под вкладкой: оставленный чек-лист — это
   * чужие или уже не положенные человеку награды на экране.
   */
  function reset(): void {
    items.value = [];
    status.value = "idle";
    inflight = null;
    rewardModalQueue.value = [];
    highlightedQuestId.value = null;
  }

  function requestOpen(questId: string) {
    highlightedQuestId.value = questId;
    openRequestToken.value++;
  }

  function enqueueRewardModal(item: OnboardingQuestItem) {
    rewardModalQueue.value.push(item);
  }

  function dismissCurrentRewardModal() {
    rewardModalQueue.value.shift();
  }

  return {
    items,
    status,
    completedCount,
    totalCount,
    hasItems,
    load,
    reset,
    openRequestToken,
    highlightedQuestId,
    requestOpen,
    rewardModalQueue,
    enqueueRewardModal,
    dismissCurrentRewardModal,
  };
});
